import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import { speak, speakSlow, speakInstruction, stopSpeaking } from '@/utils/speak';
import {
  PREK_LETTERS, PREK_NUMBERS, K1_SIGHT_WORDS, K1_CVC_WORDS,
  K1_ADDITION, K1_SUBTRACTION, K1_SHAPES,
  SKILL_MAPS, getGradeTier, TIER_ORDER, TIER_LABEL
} from '@/data/lessonData';
import {
  ArrowLeft, Volume2, Star, Trophy, Home, BookOpen, Calculator,
  ChevronRight, Award, Check, X, Sparkles, RotateCcw
} from 'lucide-react';
import ConceptTeachCard from '@/components/ConceptTeachCard';
import PracticeExitControls from '@/components/PracticeExitControls';

// Map skill.id → (module, conceptKey) so we know which concept lesson to show first.
const SKILL_CONCEPT_MAP = {
  'letter-names':   { module: 'alphabets', conceptKey: 'consonants' },
  'letter-sounds':  { module: 'alphabets', conceptKey: 'vowels' },
  'match-sounds':   { module: 'alphabets', conceptKey: 'digraphs' },
  'sight-words':    { module: 'reading',   conceptKey: 'sight_words' },
  'cvc-words':      { module: 'reading',   conceptKey: 'cvc' },
  'sentences':      { module: 'writing',   conceptKey: 'sentence' },
  'comprehension':  { module: 'reading',   conceptKey: 'comprehension' },
  'vocabulary':     { module: 'reading',   conceptKey: 'sight_words' },
  'counting':       { module: 'math',      conceptKey: 'counting' },
  'number-names':   { module: 'math',      conceptKey: 'place_value' },
  'comparing':      { module: 'math',      conceptKey: 'counting' },
  'addition':       { module: 'math',      conceptKey: 'addition' },
  'subtraction':    { module: 'math',      conceptKey: 'subtraction' },
  'shapes':         { module: 'math',      conceptKey: 'shapes' },
  'multiply':       { module: 'math',      conceptKey: 'multiplication' },
  'fractions':      { module: 'math',      conceptKey: 'fractions' },
};

// ============== SKILL MAP SCREEN ==============
const SkillMap = ({ gradeTier, subject, onSelectSkill, childName, progress }) => {
  const skills = SKILL_MAPS[gradeTier]?.[subject] || [];
  const subjectLabel = subject === 'reading' ? 'Reading' : 'Math';
  const SubjectIcon = subject === 'reading' ? BookOpen : Calculator;
  const isPrek = gradeTier === 'prek';

  // "Coming up next!" — skills from the tier immediately above this one, shown locked.
  const nextTier = TIER_ORDER[TIER_ORDER.indexOf(gradeTier) + 1];
  const upcomingSkills = (nextTier && SKILL_MAPS[nextTier]?.[subject]) || [];

  useEffect(() => {
    if (isPrek) {
      speak(`Hi ${childName || 'friend'}! Let's learn ${subjectLabel}! Tap a circle to start!`);
    }
    return () => stopSpeaking();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-white to-emerald-50 p-4 pb-24"
      data-testid="skill-map-screen">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => window.history.back()}
            className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <SubjectIcon className="w-6 h-6" style={{ color: subject === 'reading' ? '#0D9488' : '#F97316' }} />
            <h1 className={`font-bold text-slate-800 ${isPrek ? 'text-2xl' : 'text-xl'}`}>
              {subjectLabel}
            </h1>
          </div>
          {/* Grade chip so parents / kids see which band is active */}
          <span
            className="ml-2 inline-flex items-center gap-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold px-2 py-0.5"
            data-testid="grade-chip"
          >
            Grade: {TIER_LABEL[gradeTier] || 'Pre-K'}
          </span>
          {isPrek && (
            <Button variant="ghost" size="icon" className="ml-auto rounded-full"
              onClick={() => speak(`Tap a circle to start learning!`)}>
              <Volume2 className="w-5 h-5 text-blue-500" />
            </Button>
          )}
        </div>

        {/* Skill Path - vertical road map */}
        <div className="relative">
          {/* Road line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-2 bg-slate-200 rounded-full -translate-x-1/2 z-0" />

          {skills.map((skill, idx) => {
            const isEven = idx % 2 === 0;
            const completed = (progress?.[skill.id] || 0);
            const pct = Math.round((completed / skill.lessonCount) * 100);
            const unlocked = idx === 0 || (progress?.[skills[idx - 1]?.id] || 0) >= Math.ceil(skills[idx - 1]?.lessonCount * 0.5);
            return (
              <motion.div
                key={skill.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={`relative z-10 flex items-center mb-8 ${isEven ? 'justify-start' : 'justify-end'}`}
              >
                <button
                  onClick={() => unlocked && onSelectSkill(skill)}
                  disabled={!unlocked}
                  data-testid={`skill-${skill.id}`}
                  className={`group relative flex items-center gap-4 rounded-3xl p-4 pr-6 transition-all
                    ${isEven ? 'flex-row' : 'flex-row-reverse'}
                    ${unlocked
                      ? 'bg-white shadow-lg hover:shadow-xl border-2 border-transparent hover:border-emerald-300 cursor-pointer'
                      : 'bg-slate-100 opacity-60 cursor-not-allowed'}`}
                  style={{ maxWidth: '80%' }}
                  onMouseEnter={() => { if (isPrek && unlocked) speak(skill.name); }}
                >
                  {/* Circle Icon */}
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl shrink-0
                    ${pct >= 100 ? 'bg-emerald-100 ring-4 ring-emerald-400' :
                      unlocked ? 'bg-blue-50 ring-4 ring-blue-300' : 'bg-slate-200'}`}>
                    {pct >= 100 ? <Check className="w-8 h-8 text-emerald-600" /> : skill.icon}
                  </div>
                  {/* Info */}
                  <div className={isEven ? 'text-left' : 'text-right'}>
                    <p className={`font-bold text-slate-800 ${isPrek ? 'text-lg' : 'text-base'}`}>{skill.name}</p>
                    {!isPrek && <p className="text-xs text-slate-500">{skill.desc}</p>}
                    {unlocked && pct > 0 && pct < 100 && (
                      <div className="mt-1 w-24">
                        <Progress value={pct} className="h-2" />
                        <p className="text-[10px] text-slate-400 mt-0.5">{completed}/{skill.lessonCount}</p>
                      </div>
                    )}
                  </div>
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Coming up next! — locked tiles from the tier above so kids see progression */}
        {upcomingSkills.length > 0 && (
          <div className="mt-10" data-testid="coming-up-next-section">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold px-3 py-1">
                <Sparkles className="w-3 h-3" /> Coming up next!
              </span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {upcomingSkills.map((s) => (
                <div
                  key={s.id}
                  data-testid={`upcoming-${s.id}`}
                  className="relative rounded-2xl bg-white border-2 border-dashed border-slate-200 p-3 flex flex-col items-center text-center opacity-70"
                  title={`Unlock this by finishing your ${TIER_LABEL[gradeTier]} skills first!`}
                >
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center">
                    <Award className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <div className="text-3xl mb-1">{s.icon}</div>
                  <p className="text-xs font-semibold text-slate-700">{s.name}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 text-center mt-3">
              Finish your {TIER_LABEL[gradeTier]} skills to unlock these!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== LESSON ENGINE ==============
const LessonEngine = ({ skill, gradeTier, subject, childId, api, onExit }) => {
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState('concept'); // concept | teach | practice | celebrate
  const [lessonIndex, setLessonIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [totalQ, setTotalQ] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [practiceQ, setPracticeQ] = useState(null);
  const [streak, setStreak] = useState(0);
  // New: multi-game state
  const [memoryCards, setMemoryCards] = useState([]);
  const [memoryFlipped, setMemoryFlipped] = useState([]);
  const [memoryMatched, setMemoryMatched] = useState([]);
  const [sortItems, setSortItems] = useState([]);
  const [sortSelected, setSortSelected] = useState([]);
  const [dragPairs, setDragPairs] = useState({ left: [], right: [], matched: {} });
  const [dragSelected, setDragSelected] = useState(null);
  const [lessonOrder, setLessonOrder] = useState([]);

  const isPrek = gradeTier === 'prek';
  const isK1 = gradeTier === 'k1';

  // Shuffle lesson order once per skill
  useEffect(() => {
    const max = getMaxIndex();
    const indices = Array.from({ length: max }, (_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    setLessonOrder(indices);
  }, [skill.id]);

  // Map lessonIndex through shuffled order
  const getShuffledIndex = () => {
    if (lessonOrder.length === 0) return lessonIndex;
    return lessonOrder[lessonIndex % lessonOrder.length];
  };

  // Get lesson content based on skill (using shuffled order)
  const getLessonContent = useCallback(() => {
    const idx = getShuffledIndex();
    if (skill.id === 'letter-names' || skill.id === 'letter-sounds') {
      return PREK_LETTERS[idx] || null;
    }
    if (skill.id === 'counting' || skill.id === 'number-names') {
      return PREK_NUMBERS[idx] || null;
    }
    if (skill.id === 'sight-words') {
      return K1_SIGHT_WORDS[idx] || null;
    }
    if (skill.id === 'cvc-words') {
      return K1_CVC_WORDS[idx] || null;
    }
    if (skill.id === 'addition') {
      return K1_ADDITION[idx] || null;
    }
    if (skill.id === 'subtraction') {
      return K1_SUBTRACTION[idx] || null;
    }
    if (skill.id === 'shapes') {
      return K1_SHAPES[idx] || null;
    }
    if (skill.id === 'match-sounds') {
      const i = idx * 2;
      return { letters: [PREK_LETTERS[i], PREK_LETTERS[i + 1]].filter(Boolean) };
    }
    if (skill.id === 'comparing') {
      const a = Math.floor(Math.random() * 5) + 1;
      let b = Math.floor(Math.random() * 5) + 1;
      while (b === a) b = Math.floor(Math.random() * 5) + 1;
      return { groupA: a, groupB: b };
    }
    return null;
  }, [skill.id, lessonIndex, lessonOrder]);

  const content = getLessonContent();

  // Skip concept step immediately for skills we don't have a curated concept for.
  useEffect(() => {
    if (phase === 'concept' && !SKILL_CONCEPT_MAP[skill.id]) {
      setPhase('teach');
    }
  }, [phase, skill.id]);

  // Auto-speak teach phase
  useEffect(() => {
    if (phase === 'teach' && content) {
      const timer = setTimeout(() => teachSpeak(), 500);
      return () => { clearTimeout(timer); stopSpeaking(); };
    }
  }, [phase, lessonIndex]);

  const teachSpeak = () => {
    if (skill.id === 'letter-names' && content) {
      speak(`This is the letter ${content.letter}! ${content.letter} is for ${content.word}!`);
    } else if (skill.id === 'letter-sounds' && content) {
      speak(`The letter ${content.letter} makes the sound ${content.sound}! ${content.sound}! Like in ${content.word}!`);
    } else if (skill.id === 'counting' && content) {
      const countStr = content.objects.map((_, i) => i + 1).join('... ');
      speak(`Let's count to ${content.number}! ${countStr}! ${content.number} ${content.word.toLowerCase()}!`);
    } else if (skill.id === 'number-names' && content) {
      speak(`This is the number ${content.number}! ${content.number}! Can you see ${content.number} things?`);
    } else if (skill.id === 'sight-words' && content) {
      speak(`This word is ${content.word}. ${content.word}. ${content.sentence}`);
    } else if (skill.id === 'cvc-words' && content) {
      speak(`Let's sound out this word! ${content.sounds.join('... ')}... ${content.word}! ${content.word}!`);
    } else if (skill.id === 'addition' && content) {
      speak(`${content.a} plus ${content.b}! ${content.a} and ${content.b} more makes ${content.sum}!`);
    } else if (skill.id === 'subtraction' && content) {
      speak(`${content.total} take away ${content.take}! If we start with ${content.total} and take ${content.take} away, we get ${content.diff}!`);
    } else if (skill.id === 'shapes' && content) {
      speak(`This is a ${content.name}! A ${content.name} is ${content.desc}!`);
    } else if (skill.id === 'match-sounds' && content?.letters?.[0]) {
      speak(`Which picture starts with the letter ${content.letters[0].letter}?`);
    } else if (skill.id === 'comparing' && content) {
      speak(`Which group has more? Look carefully!`);
    }
  };

  const goToPractice = () => {
    setPhase('practice');
    setSelectedAnswer(null);
    setIsCorrect(null);
    setMemoryCards([]);
    setMemoryFlipped([]);
    setMemoryMatched([]);
    setSortItems([]);
    setSortSelected([]);
    setDragPairs({ left: [], right: [], matched: {} });
    setDragSelected(null);
    generatePracticeQuestion();
  };

  // Available practice types per skill
  const getPracticeTypes = () => {
    const base = ['tap-correct'];
    if (skill.id === 'letter-names') return [...base, 'fill-missing', 'sort-order', 'memory-match'];
    if (skill.id === 'letter-sounds') return [...base, 'drag-match', 'memory-match'];
    if (skill.id === 'counting') return [...base, 'sort-order', 'fill-missing'];
    if (skill.id === 'number-names') return [...base, 'memory-match', 'fill-missing'];
    if (skill.id === 'sight-words') return [...base, 'fill-missing', 'memory-match'];
    if (skill.id === 'cvc-words') return [...base, 'sort-order', 'fill-missing'];
    if (skill.id === 'addition') return [...base, 'fill-missing'];
    if (skill.id === 'subtraction') return [...base, 'fill-missing'];
    if (skill.id === 'shapes') return [...base, 'memory-match'];
    if (skill.id === 'match-sounds') return ['drag-match', 'memory-match', 'tap-correct'];
    if (skill.id === 'comparing') return ['tap-correct'];
    return base;
  };

  const generatePracticeQuestion = () => {
    const types = getPracticeTypes();
    const practiceType = types[Math.floor(Math.random() * types.length)];

    // ---- MEMORY MATCH ----
    if (practiceType === 'memory-match') {
      generateMemoryMatch();
      return;
    }
    // ---- SORT ORDER ----
    if (practiceType === 'sort-order') {
      generateSortOrder();
      return;
    }
    // ---- FILL MISSING ----
    if (practiceType === 'fill-missing') {
      generateFillMissing();
      return;
    }
    // ---- DRAG MATCH ----
    if (practiceType === 'drag-match') {
      generateDragMatch();
      return;
    }

    // ---- TAP CORRECT (default) ----
    generateTapCorrect();
  };

  // ==== MEMORY MATCH GAME ====
  const generateMemoryMatch = () => {
    let pairs = [];
    if (skill.id === 'letter-names' || skill.id === 'letter-sounds' || skill.id === 'match-sounds') {
      const pool = [...PREK_LETTERS].sort(() => Math.random() - 0.5).slice(0, 4);
      pairs = pool.map(l => [
        { id: `letter-${l.letter}`, display: l.letter, matchId: l.letter, type: 'letter', color: l.color },
        { id: `emoji-${l.letter}`, display: l.emoji, matchId: l.letter, type: 'emoji', color: l.color }
      ]).flat();
    } else if (skill.id === 'number-names') {
      const pool = [...PREK_NUMBERS].sort(() => Math.random() - 0.5).slice(0, 4);
      pairs = pool.map(n => [
        { id: `num-${n.number}`, display: String(n.number), matchId: String(n.number), type: 'number', color: n.color },
        { id: `obj-${n.number}`, display: n.objects.slice(0, 2).join(''), matchId: String(n.number), type: 'emoji', color: n.color }
      ]).flat();
    } else if (skill.id === 'sight-words') {
      const pool = [...K1_SIGHT_WORDS].sort(() => Math.random() - 0.5).slice(0, 4);
      pairs = pool.map(w => [
        { id: `word-${w.word}`, display: w.word, matchId: w.word, type: 'word', color: '#3B82F6' },
        { id: `img-${w.word}`, display: w.image, matchId: w.word, type: 'emoji', color: '#3B82F6' }
      ]).flat();
    }
    const shuffled = pairs.sort(() => Math.random() - 0.5);
    setMemoryCards(shuffled);
    setMemoryFlipped([]);
    setMemoryMatched([]);
    setPracticeQ({ type: 'memory-match', instruction: 'Find the matching pairs!' });
    setTotalQ(t => t + 1);
    setTimeout(() => speak('Find the matching pairs! Tap two cards to flip them!'), 300);
  };

  const handleMemoryTap = (index) => {
    if (memoryFlipped.length === 2) return;
    if (memoryFlipped.includes(index)) return;
    if (memoryMatched.includes(memoryCards[index].matchId)) return;

    const newFlipped = [...memoryFlipped, index];
    setMemoryFlipped(newFlipped);

    if (newFlipped.length === 2) {
      const [a, b] = newFlipped;
      if (memoryCards[a].matchId === memoryCards[b].matchId && a !== b) {
        const newMatched = [...memoryMatched, memoryCards[a].matchId];
        setMemoryMatched(newMatched);
        speak('Great match!');
        setTimeout(() => setMemoryFlipped([]), 600);
        if (newMatched.length === memoryCards.length / 2) {
          setScore(s => s + 1);
          setStreak(s => s + 1);
          setIsCorrect(true);
          setTimeout(() => moveToNext(), 1500);
        }
      } else {
        setTimeout(() => { setMemoryFlipped([]); }, 1000);
      }
    }
  };

  // ==== SORT ORDER GAME ====
  const generateSortOrder = () => {
    let items = [];
    if (skill.id === 'letter-names') {
      const start = Math.floor(Math.random() * 22);
      items = PREK_LETTERS.slice(start, start + 4).map(l => ({
        id: l.letter, display: l.letter, emoji: l.emoji, color: l.color, order: PREK_LETTERS.indexOf(l)
      }));
    } else if (skill.id === 'counting' || skill.id === 'cvc-words') {
      const start = Math.floor(Math.random() * 7);
      items = PREK_NUMBERS.slice(start, start + 4).map(n => ({
        id: String(n.number), display: String(n.number), emoji: n.objects[0], color: n.color, order: n.number
      }));
    }
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    setSortItems(shuffled);
    setSortSelected([]);
    setPracticeQ({ type: 'sort-order', correctOrder: items.map(i => i.id), instruction: 'Put them in order! Tap from first to last.' });
    setTotalQ(t => t + 1);
    setTimeout(() => speak('Put them in order! Tap from first to last!'), 300);
  };

  const handleSortTap = (item) => {
    if (sortSelected.includes(item.id)) return;
    const expectedIndex = sortSelected.length;
    const correct = practiceQ.correctOrder[expectedIndex] === item.id;

    if (correct) {
      const newSelected = [...sortSelected, item.id];
      setSortSelected(newSelected);
      speak(item.display);
      if (newSelected.length === practiceQ.correctOrder.length) {
        setScore(s => s + 1);
        setStreak(s => s + 1);
        setIsCorrect(true);
        speak('Perfect order!');
        setTimeout(() => moveToNext(), 1500);
      }
    } else {
      speak('Try again! Start from the beginning!');
      setSortSelected([]);
    }
  };

  // ==== FILL MISSING GAME ====
  const generateFillMissing = () => {
    if (skill.id === 'letter-names' || skill.id === 'sight-words') {
      const pool = skill.id === 'letter-names' ? PREK_LETTERS : K1_SIGHT_WORDS;
      const maxIdx = Math.min(pool.length - 3, 20);
      const start = Math.floor(Math.random() * maxIdx);
      const sequence = pool.slice(start, start + 3);
      const missingIdx = 1; // Always middle
      const correct = skill.id === 'letter-names' ? sequence[missingIdx].letter : sequence[missingIdx].word;
      const distractors = pool.filter(item => {
        const val = skill.id === 'letter-names' ? item.letter : item.word;
        return val !== correct;
      }).sort(() => Math.random() - 0.5).slice(0, 2);

      const options = [
        { id: correct, display: correct, isCorrect: true },
        ...distractors.map(d => ({
          id: skill.id === 'letter-names' ? d.letter : d.word,
          display: skill.id === 'letter-names' ? d.letter : d.word,
          isCorrect: false
        }))
      ].sort(() => Math.random() - 0.5);

      const display = sequence.map((item, i) => ({
        value: i === missingIdx ? '?' :
          (skill.id === 'letter-names' ? item.letter : item.word),
        isMissing: i === missingIdx,
        emoji: skill.id === 'letter-names' ? item.emoji : item.image
      }));

      setPracticeQ({ type: 'fill-missing', sequence: display, options, instruction: 'What goes in the middle?' });
      setTotalQ(t => t + 1);
      setTimeout(() => speak('What goes in the middle? Find the missing one!'), 300);
    } else if (skill.id === 'counting' || skill.id === 'number-names') {
      const start = Math.floor(Math.random() * 7) + 1;
      const nums = [start, start + 1, start + 2];
      const missingIdx = 1;
      const correct = String(nums[missingIdx]);
      const options = [
        { id: correct, display: correct, isCorrect: true },
        { id: String(nums[missingIdx] + 2), display: String(nums[missingIdx] + 2), isCorrect: false },
        { id: String(nums[missingIdx] - 1 < 1 ? nums[missingIdx] + 3 : nums[missingIdx] - 1),
          display: String(nums[missingIdx] - 1 < 1 ? nums[missingIdx] + 3 : nums[missingIdx] - 1),
          isCorrect: false }
      ].sort(() => Math.random() - 0.5);

      const display = nums.map((n, i) => ({
        value: i === missingIdx ? '?' : String(n),
        isMissing: i === missingIdx,
        emoji: PREK_NUMBERS[n - 1]?.objects?.[0] || '?'
      }));

      setPracticeQ({ type: 'fill-missing', sequence: display, options, instruction: 'What number is missing?' });
      setTotalQ(t => t + 1);
      setTimeout(() => speak('What number is missing?'), 300);
    }
  };

  // ==== DRAG MATCH GAME ====
  const generateDragMatch = () => {
    const pool = [...PREK_LETTERS].sort(() => Math.random() - 0.5).slice(0, 3);
    const left = pool.map(l => ({ id: l.letter, display: l.letter, color: l.color }));
    const right = [...pool].sort(() => Math.random() - 0.5).map(l => ({ id: l.letter, display: l.emoji, label: l.word }));
    setDragPairs({ left, right, matched: {} });
    setDragSelected(null);
    setPracticeQ({ type: 'drag-match', instruction: 'Match each letter to its picture! Tap letter, then tap picture.' });
    setTotalQ(t => t + 1);
    setTimeout(() => speak('Match each letter to its picture! Tap a letter, then tap the picture!'), 300);
  };

  const handleDragTap = (side, item) => {
    if (dragPairs.matched[item.id]) return;
    if (side === 'left') {
      setDragSelected(item.id);
      speak(item.display);
    } else if (side === 'right' && dragSelected) {
      if (item.id === dragSelected) {
        const newMatched = { ...dragPairs.matched, [item.id]: true };
        setDragPairs(p => ({ ...p, matched: newMatched }));
        setDragSelected(null);
        speak('Match!');
        if (Object.keys(newMatched).length === dragPairs.left.length) {
          setScore(s => s + 1);
          setStreak(s => s + 1);
          setIsCorrect(true);
          speak('All matched! Great job!');
          setTimeout(() => moveToNext(), 1500);
        }
      } else {
        setDragSelected(null);
        speak('Try again!');
      }
    }
  };

  // ==== TAP CORRECT (original) ====
  const generateTapCorrect = () => {
    if (skill.id === 'letter-names' || skill.id === 'letter-sounds') {
      // "Touch the letter X" or "Touch the picture that starts with X"
      const correct = content;
      const distractors = PREK_LETTERS.filter(l => l.letter !== correct.letter)
        .sort(() => Math.random() - 0.5).slice(0, 2);
      const options = [correct, ...distractors].sort(() => Math.random() - 0.5);

      if (skill.id === 'letter-names') {
        setPracticeQ({
          instruction: `Touch the letter ${correct.letter}!`,
          options: options.map(o => ({ id: o.letter, display: o.letter, label: o.letter, isCorrect: o.letter === correct.letter })),
          type: 'letter-tap'
        });
        setTimeout(() => speak(`Touch the letter ${correct.letter}!`), 300);
      } else {
        setPracticeQ({
          instruction: `Touch the picture that starts with ${correct.letter}!`,
          options: options.map(o => ({ id: o.letter, display: o.emoji, label: o.word, isCorrect: o.letter === correct.letter })),
          type: 'image-tap'
        });
        setTimeout(() => speak(`Touch the picture that starts with ${correct.letter}!`), 300);
      }
    } else if (skill.id === 'counting' || skill.id === 'number-names') {
      const correct = content;
      const wrongNums = PREK_NUMBERS.filter(n => n.number !== correct.number)
        .sort(() => Math.random() - 0.5).slice(0, 2);
      const options = [correct, ...wrongNums].sort(() => Math.random() - 0.5);
      setPracticeQ({
        instruction: `How many do you see? Touch the right number!`,
        objects: correct.objects,
        options: options.map(o => ({ id: o.number, display: String(o.number), label: o.word, isCorrect: o.number === correct.number })),
        type: 'count-tap'
      });
      setTimeout(() => speak(`How many do you see? Touch the right number!`), 300);
    } else if (skill.id === 'sight-words') {
      const correct = content;
      const distractors = K1_SIGHT_WORDS.filter(w => w.word !== correct.word)
        .sort(() => Math.random() - 0.5).slice(0, 2);
      const options = [correct, ...distractors].sort(() => Math.random() - 0.5);
      setPracticeQ({
        instruction: `Touch the word: ${correct.word}`,
        options: options.map(o => ({ id: o.word, display: o.word, label: o.word, isCorrect: o.word === correct.word })),
        type: 'word-tap'
      });
      setTimeout(() => speak(`Touch the word: ${correct.word}!`), 300);
    } else if (skill.id === 'cvc-words') {
      const correct = content;
      const distractors = K1_CVC_WORDS.filter(w => w.word !== correct.word)
        .sort(() => Math.random() - 0.5).slice(0, 2);
      const options = [correct, ...distractors].sort(() => Math.random() - 0.5);
      setPracticeQ({
        instruction: `Which picture shows: ${correct.word}?`,
        options: options.map(o => ({ id: o.word, display: o.image, label: o.word, isCorrect: o.word === correct.word })),
        type: 'image-tap'
      });
      setTimeout(() => speak(`Which picture shows... ${correct.word}?`), 300);
    } else if (skill.id === 'addition') {
      const correct = content;
      const wrongAnswers = [correct.sum + 1, correct.sum - 1, correct.sum + 2].filter(n => n > 0 && n !== correct.sum);
      const options = [
        { id: correct.sum, display: String(correct.sum), isCorrect: true },
        ...wrongAnswers.slice(0, 2).map(n => ({ id: n, display: String(n), isCorrect: false }))
      ].sort(() => Math.random() - 0.5);
      setPracticeQ({
        instruction: `${correct.a} + ${correct.b} = ?`,
        objects: [...Array(correct.a).fill(correct.emojiA), ...Array(correct.b).fill(correct.emojiB)],
        options,
        type: 'count-tap'
      });
      setTimeout(() => speak(`What is ${correct.a} plus ${correct.b}? Count them all!`), 300);
    } else if (skill.id === 'subtraction') {
      const correct = content;
      const wrongAnswers = [correct.diff + 1, correct.diff - 1, correct.diff + 2].filter(n => n >= 0 && n !== correct.diff);
      const options = [
        { id: correct.diff, display: String(correct.diff), isCorrect: true },
        ...wrongAnswers.slice(0, 2).map(n => ({ id: n, display: String(n), isCorrect: false }))
      ].sort(() => Math.random() - 0.5);
      setPracticeQ({
        instruction: `${correct.total} - ${correct.take} = ?`,
        objects: [...Array(correct.diff).fill(correct.emoji), ...Array(correct.take).fill('❌')],
        options,
        type: 'count-tap'
      });
      setTimeout(() => speak(`What is ${correct.total} take away ${correct.take}?`), 300);
    } else if (skill.id === 'shapes') {
      const correct = content;
      const distractors = K1_SHAPES.filter(s => s.name !== correct.name)
        .sort(() => Math.random() - 0.5).slice(0, 2);
      const options = [correct, ...distractors].sort(() => Math.random() - 0.5);
      setPracticeQ({
        instruction: `Touch the ${correct.name}!`,
        options: options.map(s => ({ id: s.name, display: s.emoji, label: s.name, isCorrect: s.name === correct.name })),
        type: 'image-tap'
      });
      setTimeout(() => speak(`Touch the ${correct.name}!`), 300);
    } else if (skill.id === 'match-sounds') {
      const target = content.letters?.[0];
      if (!target) return;
      const distractors = PREK_LETTERS.filter(l => l.letter !== target.letter)
        .sort(() => Math.random() - 0.5).slice(0, 2);
      const options = [target, ...distractors].sort(() => Math.random() - 0.5);
      setPracticeQ({
        instruction: `Which picture starts with ${target.letter}?`,
        options: options.map(o => ({ id: o.letter, display: o.emoji, label: o.word, isCorrect: o.letter === target.letter })),
        type: 'image-tap'
      });
      setTimeout(() => speak(`Which picture starts with the letter ${target.letter}?`), 300);
    } else if (skill.id === 'comparing') {
      const { groupA, groupB } = content;
      const emoji = '🌟';
      setPracticeQ({
        instruction: `Which group has more?`,
        groupA: Array(groupA).fill(emoji),
        groupB: Array(groupB).fill(emoji),
        correctGroup: groupA > groupB ? 'A' : 'B',
        type: 'compare-tap'
      });
      setTimeout(() => speak(`Which group has more stars? Tap the bigger group!`), 300);
    }
    setTotalQ(t => t + 1);
  };

  const handleAnswer = (option) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(option.id || option);

    const correct = option.isCorrect !== undefined ? option.isCorrect : option === practiceQ?.correctGroup;
    setIsCorrect(correct);

    if (correct) {
      setScore(s => s + 1);
      setStreak(s => s + 1);
      speak(streak >= 2 ? 'Amazing! Great job!' : 'Yes! That is right!');
    } else {
      setStreak(0);
      if (skill.id === 'letter-names') {
        speak(`Not quite. The answer is ${content.letter}. Let's try the next one!`);
      } else {
        speak('Oops! That is not right. Let us try the next one!');
      }
    }

    setTimeout(() => {
      moveToNext();
    }, 2000);
  };

  const moveToNext = () => {
    const maxIndex = getMaxIndex();
    if (lessonIndex < maxIndex - 1) {
      setLessonIndex(i => i + 1);
      setPhase('teach');
      setSelectedAnswer(null);
      setIsCorrect(null);
    } else {
      // Skill complete!
      setPhase('celebrate');
      setShowConfetti(true);
      speak(`Wow! You finished ${skill.name}! You got ${score} out of ${totalQ} right! Great job!`);
      saveProgress();
    }
  };

  const getMaxIndex = () => {
    if (skill.id === 'letter-names' || skill.id === 'letter-sounds') return Math.min(PREK_LETTERS.length, skill.lessonCount);
    if (skill.id === 'counting' || skill.id === 'number-names') return Math.min(PREK_NUMBERS.length, skill.lessonCount);
    if (skill.id === 'sight-words') return Math.min(K1_SIGHT_WORDS.length, skill.lessonCount);
    if (skill.id === 'cvc-words') return Math.min(K1_CVC_WORDS.length, skill.lessonCount);
    if (skill.id === 'addition') return Math.min(K1_ADDITION.length, skill.lessonCount);
    if (skill.id === 'subtraction') return Math.min(K1_SUBTRACTION.length, skill.lessonCount);
    if (skill.id === 'shapes') return Math.min(K1_SHAPES.length, skill.lessonCount);
    if (skill.id === 'match-sounds') return Math.min(Math.floor(PREK_LETTERS.length / 2), skill.lessonCount);
    if (skill.id === 'comparing') return skill.lessonCount;
    return skill.lessonCount;
  };

  const saveProgress = async () => {
    try {
      await api.post('/learn/progress', {
        child_id: childId,
        skill_id: skill.id,
        subject,
        score,
        total: totalQ,
        lesson_index: lessonIndex + 1
      });
    } catch (e) { console.error(e); }
  };

  const progressPct = Math.round(((lessonIndex) / getMaxIndex()) * 100);

  // ---- RENDER TEACH PHASE ----
  const renderTeach = () => {
    if (skill.id === 'letter-names' || skill.id === 'letter-sounds') {
      return (
        <motion.div
          key={`teach-${lessonIndex}`}
          initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
          className="flex flex-col items-center gap-6"
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-40 h-40 rounded-3xl flex items-center justify-center shadow-xl"
            style={{ backgroundColor: content?.color + '20', border: `4px solid ${content?.color}` }}
          >
            <span className="text-8xl font-bold" style={{ color: content?.color }}>{content?.letter}</span>
          </motion.div>
          <div className="text-center">
            <span className="text-7xl mb-2 block">{content?.emoji}</span>
            {!isPrek && <p className="text-xl font-bold text-slate-700">{content?.word}</p>}
            {skill.id === 'letter-sounds' && (
              <p className="text-lg text-slate-500 mt-1 italic">"{content?.sound}"</p>
            )}
          </div>
          <Button onClick={goToPractice} size="lg"
            className="mt-4 rounded-full px-10 py-6 text-lg font-bold bg-emerald-500 hover:bg-emerald-600"
            data-testid="practice-btn">
            <span className="mr-2 text-2xl">👆</span> Let's Practice!
          </Button>
          <Button variant="ghost" size="sm" onClick={teachSpeak} className="text-blue-500">
            <Volume2 className="w-5 h-5 mr-1" /> Hear Again
          </Button>
        </motion.div>
      );
    }

    if (skill.id === 'counting' || skill.id === 'number-names') {
      return (
        <motion.div
          key={`teach-${lessonIndex}`}
          initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
          className="flex flex-col items-center gap-6"
        >
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-36 h-36 rounded-full flex items-center justify-center shadow-xl"
            style={{ backgroundColor: content?.color + '20', border: `4px solid ${content?.color}` }}
          >
            <span className="text-7xl font-bold" style={{ color: content?.color }}>{content?.number}</span>
          </motion.div>
          {/* Animated objects appearing one by one */}
          <div className="flex flex-wrap gap-3 justify-center max-w-xs">
            {content?.objects.map((obj, i) => (
              <motion.span
                key={i}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3 + i * 0.3, type: 'spring' }}
                className="text-4xl"
              >
                {obj}
              </motion.span>
            ))}
          </div>
          <Button onClick={goToPractice} size="lg"
            className="mt-4 rounded-full px-10 py-6 text-lg font-bold bg-blue-500 hover:bg-blue-600"
            data-testid="practice-btn">
            <span className="mr-2 text-2xl">👆</span> Let's Practice!
          </Button>
          <Button variant="ghost" size="sm" onClick={teachSpeak} className="text-blue-500">
            <Volume2 className="w-5 h-5 mr-1" /> Hear Again
          </Button>
        </motion.div>
      );
    }

    if (skill.id === 'sight-words') {
      return (
        <motion.div
          key={`teach-${lessonIndex}`}
          initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="flex flex-col items-center gap-6"
        >
          <span className="text-6xl">{content?.image}</span>
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="bg-amber-50 border-4 border-amber-300 rounded-2xl px-12 py-6 shadow-lg"
          >
            <span className="text-5xl font-bold text-amber-700">{content?.word}</span>
          </motion.div>
          <p className="text-lg text-slate-600 italic">"{content?.sentence}"</p>
          <Button onClick={goToPractice} size="lg"
            className="mt-2 rounded-full px-10 py-6 text-lg font-bold bg-amber-500 hover:bg-amber-600"
            data-testid="practice-btn">
            <span className="mr-2 text-2xl">👆</span> Practice!
          </Button>
          <Button variant="ghost" size="sm" onClick={teachSpeak} className="text-blue-500">
            <Volume2 className="w-5 h-5 mr-1" /> Hear Again
          </Button>
        </motion.div>
      );
    }

    if (skill.id === 'cvc-words') {
      return (
        <motion.div
          key={`teach-${lessonIndex}`}
          initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="flex flex-col items-center gap-6"
        >
          <span className="text-6xl">{content?.image}</span>
          <div className="flex items-center gap-2">
            {content?.sounds.map((s, i) => (
              <motion.div
                key={i}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 + i * 0.4 }}
                className="bg-blue-50 border-3 border-blue-300 rounded-xl px-5 py-4"
              >
                <span className="text-4xl font-bold text-blue-700">{s}</span>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 + content?.sounds.length * 0.4 }}
            className="bg-emerald-50 border-3 border-emerald-300 rounded-2xl px-8 py-4"
          >
            <span className="text-3xl font-bold text-emerald-700">{content?.word}</span>
          </motion.div>
          <Button onClick={goToPractice} size="lg"
            className="mt-2 rounded-full px-10 py-6 text-lg font-bold bg-blue-500 hover:bg-blue-600"
            data-testid="practice-btn">
            <span className="mr-2 text-2xl">👆</span> Practice!
          </Button>
          <Button variant="ghost" size="sm" onClick={teachSpeak} className="text-blue-500">
            <Volume2 className="w-5 h-5 mr-1" /> Hear Again
          </Button>
        </motion.div>
      );
    }

    // Default: match-sounds, comparing - go straight to practice
    if (skill.id === 'match-sounds' || skill.id === 'comparing') {
      goToPractice();
      return null;
    }

    // Addition teach
    if (skill.id === 'addition' && content) {
      return (
        <motion.div key={`teach-${lessonIndex}`}
          initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="flex flex-col items-center gap-5">
          <div className="flex items-center gap-4 text-5xl">
            <div className="flex gap-1">
              {Array(content.a).fill(content.emojiA).map((e, i) => (
                <motion.span key={`a${i}`} initial={{ scale: 0 }} animate={{ scale: 1 }}
                  transition={{ delay: 0.2 + i * 0.2 }}>{e}</motion.span>
              ))}
            </div>
            <span className="text-3xl font-bold text-slate-600">+</span>
            <div className="flex gap-1">
              {Array(content.b).fill(content.emojiB).map((e, i) => (
                <motion.span key={`b${i}`} initial={{ scale: 0 }} animate={{ scale: 1 }}
                  transition={{ delay: 0.2 + (content.a + i) * 0.2 }}>{e}</motion.span>
              ))}
            </div>
          </div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.2 + (content.a + content.b) * 0.2 }}
            className="text-center">
            <span className="text-2xl text-slate-600">=</span>
            <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1, repeat: Infinity }}
              className="text-6xl font-bold ml-3" style={{ color: content.color }}>{content.sum}</motion.span>
          </motion.div>
          <Button onClick={goToPractice} size="lg"
            className="mt-2 rounded-full px-10 py-6 text-lg font-bold bg-blue-500 hover:bg-blue-600"
            data-testid="practice-btn">
            <span className="mr-2 text-2xl">👆</span> Practice!
          </Button>
          <Button variant="ghost" size="sm" onClick={teachSpeak} className="text-blue-500">
            <Volume2 className="w-5 h-5 mr-1" /> Hear Again
          </Button>
        </motion.div>
      );
    }

    // Subtraction teach
    if (skill.id === 'subtraction' && content) {
      return (
        <motion.div key={`teach-${lessonIndex}`}
          initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="flex flex-col items-center gap-5">
          <div className="flex gap-1 text-4xl flex-wrap justify-center max-w-xs">
            {Array(content.total).fill(content.emoji).map((e, i) => (
              <motion.span key={i}
                initial={{ scale: 1 }}
                animate={i >= content.diff ? { scale: 0, opacity: 0 } : { scale: 1, opacity: 1 }}
                transition={{ delay: 1.5 + (i >= content.diff ? (i - content.diff) * 0.3 : 0) }}
              >{e}</motion.span>
            ))}
          </div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.5 }}
            className="text-center">
            <p className="text-lg text-slate-600">{content.total} - {content.take} =</p>
            <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1, repeat: Infinity, delay: 3 }}
              className="text-6xl font-bold" style={{ color: content.color }}>{content.diff}</motion.span>
          </motion.div>
          <Button onClick={goToPractice} size="lg"
            className="mt-2 rounded-full px-10 py-6 text-lg font-bold bg-purple-500 hover:bg-purple-600"
            data-testid="practice-btn">
            <span className="mr-2 text-2xl">👆</span> Practice!
          </Button>
          <Button variant="ghost" size="sm" onClick={teachSpeak} className="text-blue-500">
            <Volume2 className="w-5 h-5 mr-1" /> Hear Again
          </Button>
        </motion.div>
      );
    }

    // Shapes teach
    if (skill.id === 'shapes' && content) {
      return (
        <motion.div key={`teach-${lessonIndex}`}
          initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="flex flex-col items-center gap-5">
          <motion.span
            animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-8xl">{content.emoji}</motion.span>
          <p className="text-3xl font-bold" style={{ color: content.color }}>{content.name}</p>
          <p className="text-lg text-slate-500">{content.desc}</p>
          <Button onClick={goToPractice} size="lg"
            className="mt-2 rounded-full px-10 py-6 text-lg font-bold bg-indigo-500 hover:bg-indigo-600"
            data-testid="practice-btn">
            <span className="mr-2 text-2xl">👆</span> Practice!
          </Button>
          <Button variant="ghost" size="sm" onClick={teachSpeak} className="text-blue-500">
            <Volume2 className="w-5 h-5 mr-1" /> Hear Again
          </Button>
        </motion.div>
      );
    }

    return <p className="text-center text-slate-500">Lesson coming soon!</p>;
  };

  // ---- RENDER PRACTICE PHASE ----
  const renderPractice = () => {
    if (!practiceQ) return null;

    // ==== MEMORY MATCH RENDER ====
    if (practiceQ.type === 'memory-match') {
      return (
        <motion.div key="memory" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-4 w-full max-w-lg" data-testid="memory-match-game">
          <p className={`text-center font-bold ${isPrek ? 'text-xl' : 'text-lg'} text-slate-700 mb-2`}>
            {isPrek ? '' : 'Find the matching pairs!'}
          </p>
          <Button variant="ghost" size="sm" onClick={() => speak('Find the matching pairs! Tap two cards!')} className="text-blue-500">
            <Volume2 className="w-5 h-5 mr-1" /> Hear Again
          </Button>
          <div className="grid grid-cols-4 gap-3 w-full">
            {memoryCards.map((card, i) => {
              const isFlipped = memoryFlipped.includes(i) || memoryMatched.includes(card.matchId);
              const isMatch = memoryMatched.includes(card.matchId);
              return (
                <motion.button
                  key={card.id}
                  initial={{ rotateY: 0 }}
                  animate={{ rotateY: isFlipped ? 180 : 0 }}
                  onClick={() => handleMemoryTap(i)}
                  data-testid={`memory-card-${i}`}
                  className={`aspect-square rounded-2xl border-4 flex items-center justify-center text-3xl font-bold transition-all
                    ${isMatch ? 'border-emerald-400 bg-emerald-50' :
                      isFlipped ? 'border-blue-300 bg-blue-50' :
                      'border-slate-200 bg-gradient-to-br from-blue-400 to-indigo-500 hover:from-blue-500 hover:to-indigo-600 cursor-pointer'}`}
                >
                  {isFlipped ? (
                    <span style={{ transform: 'rotateY(180deg)' }}>
                      {card.type === 'letter' || card.type === 'number' || card.type === 'word'
                        ? <span className="text-2xl" style={{ color: card.color }}>{card.display}</span>
                        : <span className="text-3xl">{card.display}</span>}
                    </span>
                  ) : (
                    <span className="text-white text-2xl">?</span>
                  )}
                </motion.button>
              );
            })}
          </div>
          <p className="text-sm text-slate-500">{memoryMatched.length} / {memoryCards.length / 2} pairs found</p>
        </motion.div>
      );
    }

    // ==== SORT ORDER RENDER ====
    if (practiceQ.type === 'sort-order') {
      return (
        <motion.div key="sort" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-6 w-full max-w-md" data-testid="sort-order-game">
          <p className={`text-center font-bold ${isPrek ? 'text-xl' : 'text-lg'} text-slate-700`}>
            {isPrek ? '' : practiceQ.instruction}
          </p>
          <Button variant="ghost" size="sm" onClick={() => speak(practiceQ.instruction)} className="text-blue-500">
            <Volume2 className="w-5 h-5 mr-1" /> Hear Again
          </Button>
          {/* Selected items (in order) */}
          <div className="flex gap-3 min-h-[70px] items-center">
            {sortSelected.map((id, i) => {
              const item = sortItems.find(s => s.id === id);
              return (
                <motion.div key={id} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}
                  className="w-14 h-14 rounded-xl border-3 border-emerald-400 bg-emerald-50 flex items-center justify-center">
                  <span className="text-2xl font-bold" style={{ color: item?.color }}>{item?.display}</span>
                </motion.div>
              );
            })}
            {Array(sortItems.length - sortSelected.length).fill(0).map((_, i) => (
              <div key={`empty-${i}`} className="w-14 h-14 rounded-xl border-3 border-dashed border-slate-300 flex items-center justify-center">
                <span className="text-slate-300 text-lg">?</span>
              </div>
            ))}
          </div>
          {/* Tap choices */}
          <div className="flex gap-3 flex-wrap justify-center">
            {sortItems.map((item) => {
              const used = sortSelected.includes(item.id);
              return (
                <motion.button
                  key={item.id}
                  whileHover={!used ? { scale: 1.1 } : {}}
                  whileTap={!used ? { scale: 0.9 } : {}}
                  onClick={() => !used && handleSortTap(item)}
                  disabled={used}
                  data-testid={`sort-item-${item.id}`}
                  className={`w-16 h-16 rounded-2xl border-4 flex flex-col items-center justify-center transition-all
                    ${used ? 'border-slate-200 bg-slate-100 opacity-40' :
                      'border-slate-200 bg-white hover:border-blue-300 shadow-md cursor-pointer'}`}
                >
                  <span className="text-2xl font-bold" style={{ color: item.color }}>{item.display}</span>
                  <span className="text-xs">{item.emoji}</span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      );
    }

    // ==== FILL MISSING RENDER ====
    if (practiceQ.type === 'fill-missing') {
      return (
        <motion.div key="fill" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-6 w-full max-w-md" data-testid="fill-missing-game">
          <p className={`text-center font-bold ${isPrek ? 'text-xl' : 'text-lg'} text-slate-700`}>
            {isPrek ? '' : practiceQ.instruction}
          </p>
          <Button variant="ghost" size="sm" onClick={() => speak(practiceQ.instruction)} className="text-blue-500">
            <Volume2 className="w-5 h-5 mr-1" /> Hear Again
          </Button>
          {/* Sequence with gap */}
          <div className="flex gap-3 items-center">
            {practiceQ.sequence.map((item, i) => (
              <div key={i} className={`w-16 h-16 rounded-2xl border-4 flex flex-col items-center justify-center
                ${item.isMissing ? 'border-dashed border-amber-400 bg-amber-50 animate-pulse' :
                  'border-slate-200 bg-white'}`}>
                {item.isMissing ? (
                  <span className="text-3xl text-amber-400">?</span>
                ) : (
                  <>
                    <span className="text-2xl font-bold text-slate-800">{item.value}</span>
                    <span className="text-xs">{item.emoji}</span>
                  </>
                )}
              </div>
            ))}
          </div>
          {/* Options */}
          <div className="flex gap-4">
            {practiceQ.options.map((opt) => {
              const isSelected = selectedAnswer === opt.id;
              const showCorrect = selectedAnswer !== null && opt.isCorrect;
              const showWrong = isSelected && !isCorrect;
              return (
                <motion.button
                  key={opt.id}
                  whileHover={selectedAnswer === null ? { scale: 1.1 } : {}}
                  whileTap={selectedAnswer === null ? { scale: 0.9 } : {}}
                  onClick={() => handleAnswer(opt)}
                  disabled={selectedAnswer !== null}
                  data-testid={`fill-option-${opt.id}`}
                  className={`w-18 h-18 px-6 py-4 rounded-2xl border-4 text-3xl font-bold transition-all
                    ${showCorrect ? 'border-emerald-400 bg-emerald-50 text-emerald-700' :
                      showWrong ? 'border-red-400 bg-red-50 text-red-700' :
                      'border-slate-200 bg-white hover:border-blue-300 shadow-md text-slate-800'}`}
                >
                  {opt.display}
                </motion.button>
              );
            })}
          </div>
          {selectedAnswer !== null && (
            <motion.div
              initial={{ scale: 0 }}
              animate={isCorrect
                ? { scale: [0, 1.35, 1], rotate: [0, -8, 8, 0] }
                : { scale: [0, 1, 1], x: [0, -8, 8, -6, 6, 0] }
              }
              transition={{ duration: 0.55 }}
              className="text-5xl select-none"
              data-testid={isCorrect ? 'feedback-correct' : 'feedback-wrong'}
            >
              {isCorrect ? '⭐' : '💪'}
            </motion.div>
          )}
        </motion.div>
      );
    }

    // ==== DRAG MATCH RENDER ====
    if (practiceQ.type === 'drag-match') {
      return (
        <motion.div key="drag" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-6 w-full max-w-lg" data-testid="drag-match-game">
          <p className={`text-center font-bold ${isPrek ? 'text-xl' : 'text-lg'} text-slate-700`}>
            {isPrek ? '' : practiceQ.instruction}
          </p>
          <Button variant="ghost" size="sm" onClick={() => speak(practiceQ.instruction)} className="text-blue-500">
            <Volume2 className="w-5 h-5 mr-1" /> Hear Again
          </Button>
          <div className="flex gap-8 w-full justify-center">
            {/* Left column (letters) */}
            <div className="flex flex-col gap-3">
              {dragPairs.left.map(item => {
                const matched = dragPairs.matched[item.id];
                const selected = dragSelected === item.id;
                return (
                  <motion.button
                    key={item.id}
                    whileHover={!matched ? { scale: 1.1 } : {}}
                    onClick={() => !matched && handleDragTap('left', item)}
                    data-testid={`drag-left-${item.id}`}
                    className={`w-16 h-16 rounded-2xl border-4 flex items-center justify-center text-3xl font-bold transition-all
                      ${matched ? 'border-emerald-400 bg-emerald-50' :
                        selected ? 'border-blue-400 bg-blue-50 ring-4 ring-blue-200 scale-110' :
                        'border-slate-200 bg-white hover:border-blue-300 shadow-md cursor-pointer'}`}
                    style={{ color: item.color }}
                  >
                    {item.display}
                  </motion.button>
                );
              })}
            </div>
            {/* Connection lines area */}
            <div className="flex items-center">
              <div className="text-slate-300 text-2xl">&#8594;</div>
            </div>
            {/* Right column (emojis) */}
            <div className="flex flex-col gap-3">
              {dragPairs.right.map(item => {
                const matched = dragPairs.matched[item.id];
                return (
                  <motion.button
                    key={item.id}
                    whileHover={!matched && dragSelected ? { scale: 1.1 } : {}}
                    onClick={() => !matched && handleDragTap('right', item)}
                    data-testid={`drag-right-${item.id}`}
                    className={`w-16 h-16 rounded-2xl border-4 flex items-center justify-center text-3xl transition-all
                      ${matched ? 'border-emerald-400 bg-emerald-50' :
                        dragSelected ? 'border-amber-300 bg-amber-50 hover:border-blue-400 cursor-pointer' :
                        'border-slate-200 bg-white'}`}
                  >
                    {item.display}
                  </motion.button>
                );
              })}
            </div>
          </div>
          <p className="text-sm text-slate-500">
            {Object.keys(dragPairs.matched).length} / {dragPairs.left.length} matched
          </p>
        </motion.div>
      );
    }

    if (practiceQ.type === 'compare-tap') {
      return (
        <motion.div key={`practice-${lessonIndex}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-6 w-full max-w-lg">
          <p className={`text-center font-bold ${isPrek ? 'text-xl' : 'text-lg'} text-slate-700`}>
            {isPrek ? '' : practiceQ.instruction}
          </p>
          <div className="flex gap-6 w-full justify-center">
            {/* Group A */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleAnswer('A')}
              disabled={selectedAnswer !== null}
              className={`flex-1 max-w-[180px] p-4 rounded-3xl border-4 transition-all min-h-[120px] flex flex-wrap gap-1 justify-center items-center content-center
                ${selectedAnswer === 'A'
                  ? (isCorrect ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50')
                  : selectedAnswer !== null && practiceQ.correctGroup === 'A'
                    ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white hover:border-blue-300'}`}
            >
              {practiceQ.groupA.map((e, i) => <span key={i} className="text-2xl">{e}</span>)}
            </motion.button>
            {/* Group B */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleAnswer('B')}
              disabled={selectedAnswer !== null}
              className={`flex-1 max-w-[180px] p-4 rounded-3xl border-4 transition-all min-h-[120px] flex flex-wrap gap-1 justify-center items-center content-center
                ${selectedAnswer === 'B'
                  ? (isCorrect ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50')
                  : selectedAnswer !== null && practiceQ.correctGroup === 'B'
                    ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white hover:border-blue-300'}`}
            >
              {practiceQ.groupB.map((e, i) => <span key={i} className="text-2xl">{e}</span>)}
            </motion.button>
          </div>
          {selectedAnswer !== null && (
            <motion.div
              initial={{ scale: 0 }}
              animate={isCorrect
                ? { scale: [0, 1.35, 1], rotate: [0, -8, 8, 0] }
                : { scale: [0, 1, 1], x: [0, -8, 8, -6, 6, 0] }
              }
              transition={{ duration: 0.55 }}
              className="text-5xl select-none"
              data-testid={isCorrect ? 'feedback-correct-grp' : 'feedback-wrong-grp'}
            >
              {isCorrect ? '🌟' : '💪'}
            </motion.div>
          )}
        </motion.div>
      );
    }

    // Standard tap options (letter-tap, image-tap, word-tap, count-tap)
    return (
      <motion.div key={`practice-${lessonIndex}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="flex flex-col items-center gap-6 w-full max-w-lg">

        {/* Show counting objects for count-tap */}
        {practiceQ.type === 'count-tap' && practiceQ.objects && (
          <div className="flex flex-wrap gap-2 justify-center mb-2">
            {practiceQ.objects.map((obj, i) => (
              <motion.span key={i} initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ delay: i * 0.15, type: 'spring' }}
                className="text-4xl">{obj}</motion.span>
            ))}
          </div>
        )}

        {/* Instruction - audio for Pre-K, text for older */}
        {!isPrek && (
          <p className="text-center font-bold text-lg text-slate-700">{practiceQ.instruction}</p>
        )}
        <Button variant="ghost" size="sm" onClick={() => speak(practiceQ.instruction)} className="text-blue-500">
          <Volume2 className="w-5 h-5 mr-1" /> Hear Again
        </Button>

        {/* Options Grid */}
        <div className={`grid gap-4 w-full
          ${practiceQ.options.length <= 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
          {practiceQ.options.map((opt, i) => {
            const isSelected = selectedAnswer === opt.id;
            const showCorrect = selectedAnswer !== null && opt.isCorrect;
            const showWrong = isSelected && !isCorrect;
            return (
              <motion.button
                key={opt.id}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: i * 0.1, type: 'spring' }}
                whileHover={selectedAnswer === null ? { scale: 1.08 } : {}}
                whileTap={selectedAnswer === null ? { scale: 0.95 } : {}}
                onClick={() => handleAnswer(opt)}
                disabled={selectedAnswer !== null}
                data-testid={`option-${opt.id}`}
                className={`rounded-3xl border-4 p-4 flex flex-col items-center justify-center transition-all min-h-[100px]
                  ${showCorrect ? 'border-emerald-400 bg-emerald-50 ring-4 ring-emerald-200' :
                    showWrong ? 'border-red-400 bg-red-50 ring-4 ring-red-200' :
                    'border-slate-200 bg-white hover:border-blue-300 hover:shadow-lg'}`}
              >
                <span className={practiceQ.type === 'letter-tap' ? 'text-5xl font-bold' :
                  practiceQ.type === 'image-tap' ? 'text-5xl' :
                  practiceQ.type === 'word-tap' ? 'text-2xl font-bold text-slate-800' :
                  'text-4xl font-bold text-slate-800'}
                  style={practiceQ.type === 'letter-tap' ? { color: content?.color } : {}}>
                  {opt.display}
                </span>
                {/* Show label under image for K-1 */}
                {isK1 && practiceQ.type === 'image-tap' && (
                  <span className="text-xs text-slate-500 mt-1">{opt.label}</span>
                )}
              </motion.button>
            );
          })}
        </div>

        {/* Feedback */}
        {selectedAnswer !== null && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-2">
            {isCorrect ? (
              <>
                <span className="text-4xl">⭐</span>
                <span className="text-lg font-bold text-emerald-600">Great job!</span>
                <span className="text-4xl">⭐</span>
              </>
            ) : (
              <>
                <span className="text-lg font-bold text-amber-600">Try again next time!</span>
                <span className="text-2xl">💪</span>
              </>
            )}
          </motion.div>
        )}
      </motion.div>
    );
  };

  // ---- RENDER CELEBRATION ----
  const renderCelebrate = () => {
    const pct = totalQ > 0 ? Math.round((score / totalQ) * 100) : 0;
    const stars = pct >= 90 ? 3 : pct >= 70 ? 2 : pct >= 50 ? 1 : 0;
    return (
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="flex flex-col items-center gap-6 text-center">
        {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
        <motion.div animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 0.5, repeat: 3 }}>
          <Trophy className="w-20 h-20 text-amber-400" />
        </motion.div>
        <h2 className="text-3xl font-bold text-slate-800">Amazing!</h2>
        <div className="flex gap-2">
          {[1, 2, 3].map(i => (
            <motion.div key={i} initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ delay: 0.3 + i * 0.2, type: 'spring' }}>
              <Star className={`w-12 h-12 ${i <= stars ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
            </motion.div>
          ))}
        </div>
        <p className="text-xl text-slate-600">
          You got <span className="font-bold text-emerald-600">{score}</span> out of <span className="font-bold">{totalQ}</span> right!
        </p>
        <div className="flex gap-3 mt-4">
          <Button onClick={onExit} variant="outline" size="lg" className="rounded-full px-8" data-testid="back-to-skills-btn">
            <Home className="w-5 h-5 mr-2" /> Back to Skills
          </Button>
          <Button onClick={() => { setLessonIndex(0); setPhase('teach'); setScore(0); setTotalQ(0); setStreak(0); setShowConfetti(false); }}
            size="lg" className="rounded-full px-8 bg-emerald-500 hover:bg-emerald-600" data-testid="replay-btn">
            <RotateCcw className="w-5 h-5 mr-2" /> Play Again
          </Button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-white to-emerald-50 p-4 pb-24"
      data-testid="lesson-engine">
      {/* Top Bar */}
      <div className="max-w-2xl mx-auto mb-6">
        <div className="flex items-center gap-3 mb-3">
          <Button variant="ghost" size="icon" onClick={() => { stopSpeaking(); onExit(); }}
            className="rounded-full" data-testid="exit-lesson-btn">
            <X className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <Progress value={progressPct} className="h-3" />
          </div>
          <div className="flex items-center gap-1 bg-amber-100 px-3 py-1 rounded-full">
            <Star className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-bold text-amber-700">{score}</span>
          </div>
        </div>
        {streak >= 3 && (
          <motion.div initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="text-center text-sm font-bold text-orange-500">
            {streak} in a row! 🔥
          </motion.div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto flex flex-col items-center pt-4">
        <AnimatePresence mode="wait">
          {phase === 'concept' && SKILL_CONCEPT_MAP[skill.id] && (
            <ConceptTeachCard
              key={`concept-${skill.id}`}
              module={SKILL_CONCEPT_MAP[skill.id].module}
              conceptKey={SKILL_CONCEPT_MAP[skill.id].conceptKey}
              ageGroup={gradeTier === 'prek' ? 'preschool' : (gradeTier === 'k1' ? 'early_elementary' : 'elementary')}
              storageKey={`zazu.taught.${childId}.${skill.id}`}
              onDone={() => setPhase('teach')}
            />
          )}
          {phase === 'teach' && renderTeach()}
          {phase === 'practice' && renderPractice()}
          {phase === 'celebrate' && renderCelebrate()}
        </AnimatePresence>
      </div>

      {/* Always-visible exit + pause controls during any active phase */}
      {phase !== 'celebrate' && (
        <PracticeExitControls
          childId={childId}
          onExit={() => { stopSpeaking(); onExit(); }}
          onPause={() => { stopSpeaking(); }}
        />
      )}
    </div>
  );
};

// ============== MAIN LEARN PAGE ==============
const LearnPage = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();

  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('subjects'); // subjects | skills | lesson
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [skillProgress, setSkillProgress] = useState({});
  const [gradeTier, setGradeTier] = useState('prek');

  useEffect(() => {
    fetchChild();
    return () => stopSpeaking();
  }, [childId]);

  const fetchChild = async () => {
    try {
      const [childRes, progressRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/learn/progress/${childId}`).catch(() => ({ data: { skills: {} } }))
      ]);
      setChild(childRes.data);
      setGradeTier(getGradeTier(childRes.data));
      setSkillProgress(progressRes.data?.skills || {});
    } catch (error) {
      toast.error('Could not load learner data');
    } finally {
      setLoading(false);
    }
  };

  const isPrek = gradeTier === 'prek';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-100 to-white">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
          <Sparkles className="w-10 h-10 text-emerald-500" />
        </motion.div>
      </div>
    );
  }

  // LESSON VIEW
  if (view === 'lesson' && selectedSkill) {
    return (
      <LessonEngine
        skill={selectedSkill}
        gradeTier={gradeTier}
        subject={selectedSubject}
        childId={childId}
        api={api}
        onExit={() => { setView('skills'); setSelectedSkill(null); fetchChild(); }}
      />
    );
  }

  // SKILL MAP VIEW
  if (view === 'skills' && selectedSubject) {
    return (
      <SkillMap
        gradeTier={gradeTier}
        subject={selectedSubject}
        childName={child?.name}
        progress={skillProgress}
        onSelectSkill={(skill) => { setSelectedSkill(skill); setView('lesson'); }}
      />
    );
  }

  // SUBJECT SELECTION VIEW (home)
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-white to-emerald-50 p-4"
      data-testid="learn-subjects-screen">
      <div className="max-w-md mx-auto pt-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className={`font-bold text-slate-800 ${isPrek ? 'text-2xl' : 'text-xl'}`}>
              Hi{child?.name ? `, ${child.name}` : ''}!
            </h1>
            <p className="text-sm text-slate-500">What do you want to learn?</p>
          </div>
        </div>

        {/* Subject Cards - big and visual for Pre-K */}
        <div className="space-y-4">
          <motion.button
            initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            onClick={() => { setSelectedSubject('reading'); setView('skills'); if (isPrek) speak('Reading! Let us learn letters and words!'); }}
            data-testid="subject-reading"
            className="w-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-3xl p-6 text-left text-white shadow-lg hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-4xl">
                📖
              </div>
              <div>
                <p className={`font-bold ${isPrek ? 'text-2xl' : 'text-xl'}`}>Reading</p>
                <p className="text-sm opacity-90">
                  {isPrek ? 'Letters & Sounds' : isK1(gradeTier) ? 'Words & Sentences' : 'Stories & Comprehension'}
                </p>
              </div>
              <ChevronRight className="w-8 h-8 ml-auto opacity-70" />
            </div>
          </motion.button>

          <motion.button
            initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.1 }}
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            onClick={() => { setSelectedSubject('math'); setView('skills'); if (isPrek) speak('Math! Let us learn numbers and counting!'); }}
            data-testid="subject-math"
            className="w-full bg-gradient-to-r from-blue-400 to-indigo-500 rounded-3xl p-6 text-left text-white shadow-lg hover:shadow-xl transition-shadow"
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-4xl">
                🔢
              </div>
              <div>
                <p className={`font-bold ${isPrek ? 'text-2xl' : 'text-xl'}`}>Math</p>
                <p className="text-sm opacity-90">
                  {isPrek ? 'Counting & Numbers' : isK1(gradeTier) ? 'Addition & Subtraction' : 'Multiplication & Fractions'}
                </p>
              </div>
              <ChevronRight className="w-8 h-8 ml-auto opacity-70" />
            </div>
          </motion.button>
        </div>
      </div>
    </div>
  );
};

// Helper to check K1 tier
const isK1 = (tier) => tier === 'k1';

export default LearnPage;
